/**
 * Klaret Application Shell — two-context staff navigation.
 *
 * SYSTEM context  (isInsideWorkspace = false):
 *   Sidebar = SYSTEM_NAV tree (Workspaces, Lead Pool, System Settings, Attention,
 *             Compliance, Org Analytics, Onboarding + gear-style user settings).
 *
 * WORKSPACE context (isInsideWorkspace = true):
 *   Sidebar = WORKSPACE_NAV tree (Overview, Inbox, Attention Queue, Campaigns →
 *             Sequences/Templates, Leads & ICPs, Emails & Domains,
 *             Agents, Knowledge Base, Client Chat, Workspace Settings).
 *
 * Topbar (both contexts): 56 px, canvas 92% + blur.
 *   Left  — logo wordmark + context badge.
 *   Right  — notification bell + UserMenu + workspace affordance.
 *
 * Sidebar: 264 px expanded / 64 px rail. Collapses to a drawer below 1 024 px.
 * Active item: fill icon + body-medium + --tvl-selected + 2 px claret inset.
 * Role-gating: UX only; backend @Roles is authoritative.
 *
 * Constraints: no Lucide, no Radix, no #fff/#000, role tokens only.
 */
import * as React from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppShell,
  MenuLine,
  useAppShell,
  LogoWordmark,
  Sidebar,
  SidebarGroup,
  SidebarItem,
  UserMenu,
  Breadcrumbs,
  type BreadcrumbItem,
  Settings1Line,
  LeftSmallLine,
  useMediaQuery,
} from '@teivor/shared-ui';

import { useBackTarget, NavDepthTracker, navigateWithPush } from '../lib/nav-history';
import { useSwipeBack, isStandaloneDisplay } from '../lib/use-swipe-back';
import { useRouteTransition } from '../lib/use-route-transition';
import { NavAccordion } from '../components/NavAccordion';
import { RouteProgress, PageLoader } from '../components/RouteProgress';
import { PageErrorBoundary } from '../components/PageErrorBoundary';
import { ServiceStatusBanner } from '../components/ServiceStatusBanner';
import { NotificationBell } from '../routes/ntf/feed/NotificationBell';
import { useChatThread } from '../routes/com';
import { useAttentionBadge } from '../routes/lhd/queries';
import { SessionWarningGate } from '../components/SessionWarningGate';
import {
  useIsInsideWorkspace,
  useLeaveWorkspace,
  useSelectedWorkspaceId,
} from '../lib/workspace-context';
import { useWorkspacesQuery } from '../features/admin/queries';
import {
  detectActiveGroup,
  findActiveSection,
  visibleSystemNav,
  visibleWorkspaceNav,
  type NavNode,
} from '../lib/nav';
import { useMeQuery } from '../features/settings/queries';
import { applyTheme, getTheme, type Theme } from '../lib/theme';

// ─── Per-icon nav hover motion (#1) ─────────────────────────────────────────────
// Each left-nav glyph gets its own thematic, one-shot hover animation (Tailwind
// group-hover transforms; the SidebarItem icon wrapper carries transition-transform,
// so the motion plays once on hover and settles back on leave — no perpetual spin).
// Only the settings/logs gear gets a hover motion — a gentle roll. Every other nav
// icon has no hover animation: unmapped ids resolve to '' at the call site, which
// overrides the SidebarItem default. Arbitrary value (rotate-[Ndeg]) so Tailwind v4
// always emits it (v4 trims the small named rotate scale rotate-3/6/12 that v3 had).
const NAV_ICON_HOVER: Record<string, string> = {
  workspaceSettings: 'group-hover:rotate-[45deg]', // gear rolls a bit
  logs: 'group-hover:rotate-[45deg]', // gear rolls a bit (system "logs" reuses the gear glyph)
};

// ─── Shared nav sidebar ────────────────────────────────────────────────────────

function NavSidebar({ nodes, pathname }: { nodes: NavNode[]; pathname: string }) {
  const { t } = useTranslation();
  const { railExpanded } = useAppShell();
  const navigate = useNavigate();
  // Unread badge for the workspace Client Chat item (AM POV) — only fetched when
  // the clientChat node is present (i.e. workspace context).
  const ws = useSelectedWorkspaceId();
  const hasClientChat = nodes.some((n) => n.id === 'clientChat');
  const chatUnread = useChatThread(ws, { enabled: hasClientChat }).data?.amUnreadCount ?? 0;

  // New-reply badge for the workspace Inbox item. Deliberately its own endpoint rather than the
  // attention queue's `total`: the queue lists every open card, and counting all of them made this
  // read 16 while no positive reply had arrived in two weeks — 6 OOO auto-reply snoozes plus
  // SUPPRESS/SNOOZE drafts, none of which are anyone waiting on us. The badge now counts only a draft
  // email a human must approve. Everything else is still in the queue, just not announced.
  const hasInbox = nodes.some((n) => n.id === 'inbox');
  const inboxAttention = useAttentionBadge(ws ?? '', { enabled: hasInbox }).data?.count ?? 0;

  // Boundary-aware, most-specific match across the node's prefix AND its item paths,
  // so a section stays highlighted on sub-pages that live under a different prefix
  // (e.g. Campaigns owns /ws/sequences + /ws/templates; Compliance owns /moa/audit-log).
  const activeNode = findActiveSection(nodes, pathname);

  return (
    <Sidebar collapsed={!railExpanded}>
      <SidebarGroup>
        {nodes.map((node) => {
          const isActive = node.id === activeNode?.id;
          const Icon = isActive ? node.glyph.fill : node.glyph.line;
          const to = node.items[0]?.to ?? '/';
          const badge =
            node.id === 'clientChat' && chatUnread > 0
              ? chatUnread
              : node.id === 'inbox' && inboxAttention > 0
                ? inboxAttention
                : undefined;
          return (
            <SidebarItem
              key={node.id}
              to={to}
              label={t(node.labelKey)}
              icon={<Icon size={20} color="currentColor" aria-hidden />}
              active={isActive}
              badge={badge}
              // Only the gear (settings/logs) rolls on hover; every other icon has no motion
              // ('' overrides the SidebarItem default grow).
              iconHoverClass={NAV_ICON_HOVER[node.id] ?? ''}
              onClick={(e) => {
                // SidebarItem renders <a href> — intercept for SPA navigation.
                e.preventDefault();
                navigate(to);
              }}
            />
          );
        })}
      </SidebarGroup>
    </Sidebar>
  );
}

// ─── Section subtab (Supabase-style second sidebar) ────────────────────────────

function SectionSubtab({ nodes, pathname }: { nodes: NavNode[]; pathname: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The active top-level section; only sections with 2+ sub-pages get a second sidebar.
  const active = findActiveSection(nodes, pathname);
  if (!active || active.items.length < 2) return null;
  // Only the single most-specific item is active. Items can share a prefix
  // (e.g. /shell/lead-pool is the parent of /shell/lead-pool/history), so a plain
  // prefix test would light up both parent and child — longest matching `to` wins.
  const activeItemTo = [...active.items]
    .filter((it) => pathname === it.to || pathname.startsWith(`${it.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;
  return (
    <nav aria-label={t(active.labelKey)} className="flex flex-col gap-0.5 p-3">
      <div className="px-2 pb-1.5 text-[11px] font-[600] uppercase tracking-wide text-[var(--tvl-ink-3)]">
        {t(active.labelKey)}
      </div>
      {active.items.map((item) => {
        const isActive = item.to === activeItemTo;
        return (
          <a
            key={item.to}
            href={item.to}
            onClick={(e) => {
              e.preventDefault();
              navigate(item.to);
            }}
            aria-current={isActive ? 'page' : undefined}
            data-pop
            className={[
              'flex h-8 items-center rounded-[var(--tvl-r2)] px-3 text-[13px] leading-[20px]',
              'outline-none transition-colors duration-[var(--tvl-d-fast)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--tvl-focus)]',
              isActive
                ? 'bg-[var(--tvl-selected)] font-[500] text-[var(--tvl-ink)]'
                : 'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
            ].join(' ')}
          >
            {t(item.labelKey)}
          </a>
        );
      })}
    </nav>
  );
}

// ─── Mobile drawer nav ─────────────────────────────────────────────────────────

/**
 * The accordion panel plus drawer dismissal. Rendered as AppShell's `mobileNav`,
 * so it sits INSIDE the shell's context provider and can therefore close the
 * drawer itself — a tapped nav row that leaves the drawer covering the page it
 * just opened is the thing that made the old drawer feel broken.
 */
function DrawerNav({ nodes, pathname }: { nodes: NavNode[]; pathname: string }) {
  const { closeMobileDrawer } = useAppShell();
  return <NavAccordion nodes={nodes} pathname={pathname} onNavigate={closeMobileDrawer} />;
}

// ─── Topbar ────────────────────────────────────────────────────────────────────

/**
 * Back control for the phone. Renders nothing on a drawer destination (there is nothing to go back
 * FROM) and nothing on desktop, which keeps breadcrumbs and browser navigation.
 */
/**
 * Opens the mobile navigation ROOT. Mobile only — desktop has the rail and section column.
 * Hidden while already inside the menu stack, where the back control is the way out.
 */
function MobileMenuButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isInsideWorkspace = useIsInsideWorkspace();
  if (pathname === '/menu' || pathname.startsWith('/menu/')) return null;
  if (pathname === '/ws/menu' || pathname.startsWith('/ws/menu/')) return null;
  return (
    <button
      type="button"
      onClick={() => navigateWithPush(navigate, isInsideWorkspace ? '/ws/menu' : '/menu', 'forward')}
      aria-label={t('nav.menu', 'Menu')}
      // Matches the back control's pill so the two read as one family of phone buttons.
      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--tvl-inset)] text-[var(--tvl-ink)] active:bg-[var(--tvl-wash-pressed)] md:hidden"
    >
      <MenuLine size={20} color="currentColor" aria-hidden />
    </button>
  );
}

function MobileBackButton() {
  const { t } = useTranslation();
  const { canGoBack, goBack } = useBackTarget();
  if (!canGoBack) return null;
  return (
    <button
      type="button"
      onClick={goBack}
      aria-label={t('nav.back', 'Zpět')}
      data-pop
      // Circular, filled, floating — the iOS 18 back affordance. A flat glyph sitting in a bar is
      // a web toolbar button; the pill is what reads as a phone control, and the tinted background
      // also makes it findable over content rather than only over the header.
      className="-ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--tvl-inset)] text-[var(--tvl-ink)] active:bg-[var(--tvl-wash-pressed)] md:hidden"
    >
      <LeftSmallLine size={22} aria-hidden />
    </button>
  );
}

function ShellTopbar() {
  const navigate = useNavigate();
  const { i18n: i18nInst, t } = useTranslation();
  const me = useMeQuery();
  const isInsideWorkspace = useIsInsideWorkspace();
  const selectedWorkspaceId = useSelectedWorkspaceId();
  const leaveWorkspace = useLeaveWorkspace();
  const { data: workspacesData } = useWorkspacesQuery();

  const [theme, setTheme] = React.useState<Theme>(() => getTheme());
  const handleThemeChange = React.useCallback((t: Theme) => {
    applyTheme(t);
    setTheme(t);
  }, []);

  const currentWorkspace = React.useMemo(() => {
    if (!selectedWorkspaceId || !workspacesData?.items) return null;
    return workspacesData.items.find((w) => w.id === selectedWorkspaceId) ?? null;
  }, [selectedWorkspaceId, workspacesData]);

  const handleLeaveWorkspace = React.useCallback(() => {
    leaveWorkspace();
    navigate('/shell/workspaces');
  }, [leaveWorkspace, navigate]);

  const profile = me.data;
  const displayName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || profile?.email || '';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '?';

  return (
    <div
      className="flex h-14 w-full items-center gap-3 px-3 md:px-4"
      style={{ fontFamily: 'var(--tvl-font-ui)' }}
    >
      {/* Mobile menu. NAVIGATES to the menu screen rather than opening an overlay drawer — under
          the Settings-style drill-down the menu is the root of the stack, not a sheet pulled over
          the page. Kept as a push so the back control returns you to whatever you were reading. */}
      <MobileMenuButton />

      {/* Back — mobile only, and only on routes the drawer cannot return you from. Installed to the
          Home Screen there is no browser chrome and therefore no back button, so without this a
          detail screen is a dead end: the drawer only jumps to section roots. Hidden on desktop,
          which has breadcrumbs, a rail and real browser navigation. */}
      <MobileBackButton />

      {/* Logo + context badge. DESKTOP ONLY: on a phone the wordmark is permanent chrome that
          never changes and never helps — Settings does not put the OS name on every screen. The
          bar's job on mobile is to say where you ARE (see MobilePageTitle) and how to get back. */}
      <NavLink
        to={isInsideWorkspace ? '/ws/overview' : '/shell/workspaces'}
        aria-label="Teivor"
        // data-pop marks a link that behaves as a control. Besides the shared
        // press animation it is what the mobile touch-target rule keys on — the
        // wordmark is a 28px-tall "go home" button, and it was the single most
        // common undersized target in the app because it sits on every route.
        data-pop
        className="hidden shrink-0 items-center gap-2 outline-none focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px] rounded-[var(--tvl-r1)] md:flex"
      >
        <LogoWordmark className="h-7 w-auto text-[var(--tvl-ink)]" />
      </NavLink>

      {/* No page title in the bar. Every kit page already renders its own <PageHeader> heading, so
          putting the same words in the bar showed "Přehled" twice on one screen — the duplicated-
          heading look that reads as a web app, and the opposite of what it was added for. The bar's
          job on mobile is the back control; the page names itself. */}

      {/* Context label */}
      {isInsideWorkspace && currentWorkspace ? (
        <span
          className="hidden md:inline-block shrink-0 text-[12px] font-[500] leading-[16px] text-[var(--tvl-ink-2)]"
          title={t('nav.workspace_context_label', 'Workspace context')}
        >
          {currentWorkspace.name}
        </span>
      ) : (
        <span className="hidden md:inline-block shrink-0 text-[12px] font-[500] leading-[16px] text-[var(--tvl-ink-2)]">
          {t('nav.system_context_label', 'System')}
        </span>
      )}

      <div className="ml-auto flex items-center gap-1">
        {/* Notification bell — ntf feed panel */}
        <NotificationBell />

        {/* Workspace context affordance */}
        {isInsideWorkspace ? (
          <button
            type="button"
            onClick={handleLeaveWorkspace}
            title={t('nav.leave_workspace', 'Leave workspace')}
            className={[
              'hidden md:inline-flex h-9 items-center gap-1.5 rounded-[var(--tvl-r2)] px-3',
              'text-[14px] leading-[20px] text-[var(--tvl-ink-2)]',
              'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
              'transition-colors duration-[var(--tvl-d-fast)]',
              'outline-none focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
            ].join(' ')}
          >
            <Settings1Line size={16} color="currentColor" aria-hidden />
            <span>{t('nav.leave_workspace', 'Leave workspace')}</span>
          </button>
        ) : (
          workspacesData?.items && workspacesData.items.length > 0 ? (
            <button
              type="button"
              onClick={() => {
                // Navigate to workspace picker; entering is done from the picker tile.
                navigate('/shell/workspaces');
              }}
              className={[
                'hidden md:inline-flex h-9 items-center gap-1.5 rounded-[var(--tvl-r2)] px-3',
                'text-[14px] leading-[20px] text-[var(--tvl-ink-2)]',
                'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
                'transition-colors duration-[var(--tvl-d-fast)]',
                'outline-none focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
              ].join(' ')}
            >
              {t('nav.enter_workspace', 'Enter workspace')}
            </button>
          ) : null
        )}

        {/* User menu. DESKTOP ONLY — on a phone the account belongs in the menu screen as a row
            (the Apple Account row at the top of Settings), not as a permanent avatar in the bar.
            The mobile equivalent lives in MenuPage. */}
        <UserMenu
          className="hidden md:inline-flex"
          name={displayName}
          email={profile?.email}
          avatarUrl={profile?.avatarUrl ?? undefined}
          initials={initials}
          theme={theme}
          onThemeChange={handleThemeChange}
          language={i18nInst.language}
          languages={[
            { code: 'cs', label: 'Čeština' },
            { code: 'en', label: 'English' },
          ]}
          onLanguageChange={(code) => void i18nInst.changeLanguage(code)}
          onProfile={() => navigate('/settings/profile')}
          onSettings={() => navigate('/settings/preferences')}
          onSignOut={() => {
            // POST /api/iam/auth/logout then redirect.
            void fetch('/api/iam/auth/logout', { method: 'POST', credentials: 'include' }).finally(
              () => navigate('/login'),
            );
          }}
        />
      </div>
    </div>
  );
}

// ─── Breadcrumbs (system context) ─────────────────────────────────────────────

function PageBreadcrumbs() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const active = React.useMemo(() => detectActiveGroup(pathname), [pathname]);

  const crumbs = React.useMemo<BreadcrumbItem[]>(() => {
    const link = (to: string, label: React.ReactNode): BreadcrumbItem => ({
      label,
      to,
      onClick: (e) => {
        e.preventDefault();
        navigate(to);
      },
    });
    const out: BreadcrumbItem[] = [link('/shell/workspaces', t('nav.workspaces', 'Workspaces'))];
    const sectionTo = active.items[0]?.to ?? active.pathPrefix;
    out.push(link(sectionTo, t(active.labelKey)));
    const item = [...active.items]
      .sort((a, b) => b.to.length - a.to.length)
      .find((it) => pathname === it.to || pathname.startsWith(`${it.to}/`));
    if (item && item.to !== sectionTo) {
      out.push({ label: t(item.labelKey) });
    } else if (item) {
      out[out.length - 1] = { label: t(item.labelKey) };
    }
    return out;
  }, [pathname, active, navigate, t]);

  return <Breadcrumbs items={crumbs} />;
}

// ─── AppLayout root ────────────────────────────────────────────────────────────

export function AppLayout() {
  const location = useLocation();
  const pathname = location.pathname;
  const me = useMeQuery();
  const role = me.data?.role;

  const isInsideWorkspace = useIsInsideWorkspace();
  const selectedWorkspaceId = useSelectedWorkspaceId();

  /**
   * Workspace context is derived from the PATH alone (`/ws/*`), while the workspace ID comes from
   * `?ws=` or localStorage. Nothing kept those two in step, so it was possible — and on the
   * installed app it was the DEFAULT — to be "inside a workspace" with no workspace.
   *
   * The manifest's `start_url` is `/ws/overview` with no `?ws=`, so every Home Screen launch landed
   * in workspace context. With empty localStorage (fresh install, a cleared site, a different
   * storage partition) the id was null, every workspace-scoped request went out without
   * `x-workspace-id`, and the API answered `403 No workspace context` — an app that errors on open.
   *
   * Evaluated here but RETURNED AT THE BOTTOM, after every hook: an early return above them skips
   * hooks on this render and runs them on the next, which is the "rendered fewer hooks than
   * expected" crash (the same rules-of-hooks violation recorded as UI-6 in the bug tracker).
   */
  const workspacesQuery = useWorkspacesQuery();

  /**
   * A STALE id produces the identical symptom: localStorage still holds a workspace that was since
   * deleted, or that belongs to a different account after a re-login. Context is `/ws/*`, the id is
   * non-null so the guard above is satisfied, and every request carries an `x-workspace-id` the API
   * rejects — the same "opens onto errors" the missing-id case caused.
   *
   * Gated on `isSuccess`: while the list is loading the id resolves to nothing, and redirecting on
   * that would bounce EVERY cold start to the picker before the answer arrives. Only act once we
   * actually know the id is not in the user's set.
   */
  const workspaceIsUnknown =
    isInsideWorkspace &&
    !!selectedWorkspaceId &&
    workspacesQuery.isSuccess &&
    !(workspacesQuery.data?.items ?? []).some((w) => w.id === selectedWorkspaceId);

  const strandedWithoutWorkspace =
    (isInsideWorkspace && !selectedWorkspaceId) || workspaceIsUnknown;

  // Role-gated nav nodes for each context.
  const systemNodes = React.useMemo(() => visibleSystemNav(role), [role]);
  const workspaceNodes = React.useMemo(() => visibleWorkspaceNav(role), [role]);

  // Which set of nodes is currently active.
  const activeNodes = isInsideWorkspace ? workspaceNodes : systemNodes;

  // Only reserve the section column when the active section actually has sub-pages
  // (≥2 items). Mirrors SectionSubtab's own guard so AppShell doesn't render an empty
  // 220px "second sidebar" on pages with no sub-nav (e.g. /admin/*, or single-item
  // sections like Overview / Inbox / Org analytics).
  const activeSection = findActiveSection(activeNodes, pathname);
  const hasSubtab = !!activeSection && activeSection.items.length >= 2;

  // For legacy breadcrumbs (system context only).
  const activeGroup = React.useMemo(() => detectActiveGroup(pathname), [pathname]);

  // Hide breadcrumbs on picker root.
  const isPickerRoot = pathname === '/shell/workspaces' || pathname === '/';
  const showBreadcrumbs = !isInsideWorkspace && !isPickerRoot && activeGroup.id !== 'workspaces';

  // Sidebar content depends on context.
  const sidebarContent = isInsideWorkspace ? (
    <NavSidebar nodes={workspaceNodes} pathname={pathname} />
  ) : (
    <NavSidebar nodes={systemNodes} pathname={pathname} />
  );

  // Edge-swipe-back, bound to the real scroll container so it is reachable across the whole
  // viewport — including below content shorter than the screen. Enabled only where there is
  // something to go back from; the hook itself additionally refuses outside standalone mode, where
  // iOS still performs its own back and ours would pop a second entry.
  const mainRef = React.useRef<HTMLElement | null>(null);
  const { canGoBack, goBack } = useBackTarget();
  useSwipeBack({ surfaceRef: mainRef, enabled: canGoBack, onBack: goBack });

  // Motion + pull-to-refresh are phone idioms; a mouse gets neither. `isMobile` is the same
  // 768px breakpoint the shell uses to choose the drawer, so the three cannot disagree.
  const isMobile = !useMediaQuery('(min-width: 768px)');
  useRouteTransition({ surfaceRef: mainRef, enabled: isMobile });



  // Every hook above has run, so this return is safe. `replace` so the broken URL never becomes a
  // history entry the back control could return to.
  if (strandedWithoutWorkspace) {
    return <Navigate to="/shell/workspaces" replace />;
  }

  return (
    <>
      <RouteProgress />
      <NavDepthTracker />
      <SessionWarningGate />
      <AppShell
        mainRef={mainRef}
        banner={<ServiceStatusBanner />}
        topbar={<ShellTopbar />}
        sidebar={sidebarContent}
        subtab={hasSubtab ? <SectionSubtab nodes={activeNodes} pathname={pathname} /> : undefined}
        // The ONE mobile navigation. Previously the phone carried three
        // overlapping surfaces — this drawer (flat list + detached sub-list), an
        // on-page chip strip, and a bottom tab bar with a "More" sheet. All
        // replaced by a single accordion panel opened from the topbar.
        mobileNav={<DrawerNav nodes={activeNodes} pathname={pathname} />}
      >
        {/* Breadcrumbs are desktop-only. Measured at 375px: the three Czech
            labels of a trail each lose 20-26px to ellipsis ("Pracovní prostory"
            needs 120px in 94px), so the trail became three truncated stubs that
            told you nothing. The accordion nav already shows section + sub-page,
            which is what the trail was for. */}
        {showBreadcrumbs ? (
          <div className="hidden px-4 pt-3 md:block md:px-6">
            <PageBreadcrumbs />
          </div>
        ) : null}


        <React.Suspense fallback={<PageLoader />}>
          <PageErrorBoundary routeKey={pathname}>
            {/* Only the home-indicator inset now — there is no bottom bar to clear.
                `flex-1` (deliberately WITHOUT `min-h-0`) makes this wrapper at least as tall
                as the remaining frame, so a page that asks to fill it — `lg:h-full` on
                PageContainer — has a definite height to resolve against. Omitting min-h-0
                is the safe half: the wrapper can grow past the frame for a long page and
                <main> scrolls as before, but it can never shrink below its content. */}
            <div className="flex-1 pb-[env(safe-area-inset-bottom)] md:pb-0">
              <Outlet />
            </div>
          </PageErrorBoundary>
        </React.Suspense>
      </AppShell>
    </>
  );
}
